import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { JSX } from "react";

// ─── Hash format: #/{module}/{entityType}/{entityId} ────────────────

// Old module IDs that were renamed — support existing bookmarks/history.
const LEGACY_MODULE_ALIASES: Readonly<Record<string, string>> = {
  emails: "email",
  files: "file",
};

function parseHash(): {
  moduleId: string;
  entityType?: string;
  entityId?: string;
} {
  const hash = window.location.hash.replace(/^#\/?/, "");
  const parts = hash.split("/");
  // Default landing module: `episodes` (the Agent module) — a CORE module that
  // is always registered. `contacts` is a plugin that may not be loaded, which
  // lands on "Module not found".
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const rawModuleId = parts[0] || "episodes";
  return {
    moduleId: LEGACY_MODULE_ALIASES[rawModuleId] ?? rawModuleId,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    entityType: parts[1] || undefined,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    entityId: parts[2] || undefined,
  };
}

function buildHash(
  moduleId: string,
  entityType?: string,
  entityId?: string,
): string {
  if (entityType && entityId) {
    return `#/${moduleId}/${entityType}/${entityId}`;
  }
  if (entityType) {
    return `#/${moduleId}/${entityType}`;
  }
  return `#/${moduleId}`;
}

export interface RouterContextValue {
  readonly activeModuleId: string;
  readonly entityType?: string;
  readonly entityId?: string;
  readonly pendingItemId?: string;
  readonly setActiveModule: (moduleId: string) => void;
  readonly navigate: (
    moduleId: string,
    entityType?: string,
    entityId?: string,
  ) => void;
  readonly setSelection: (
    entityType: string,
    entityId: string | undefined,
  ) => void;
  readonly consumePendingItem: () => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [activeModuleId, setActiveModuleId] = useState<string>(
    () => parseHash().moduleId,
  );
  const [entityType, setEntityType] = useState<string | undefined>(
    () => parseHash().entityType,
  );
  const [entityId, setEntityId] = useState<string | undefined>(
    () => parseHash().entityId,
  );
  const [pendingItemId, setPendingItemId] = useState<string | undefined>();

  // Sync hash -> state (back/forward buttons, manual URL edit)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const onHashChange = () => {
      const parsed = parseHash();
      setActiveModuleId(parsed.moduleId);
      setEntityType(parsed.entityType);
      setEntityId(parsed.entityId);
    };
    window.addEventListener("hashchange", onHashChange);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { window.removeEventListener("hashchange", onHashChange); };
  }, []);

  // Sync state -> hash
  useEffect(() => {
    const target = buildHash(activeModuleId, entityType, entityId);
    if (window.location.hash !== target) {
      window.location.hash = target;
    }
  }, [activeModuleId, entityType, entityId]);

  const setActiveModule = useCallback((moduleId: string) => {
    setActiveModuleId(moduleId);
    setEntityType(undefined);
    setEntityId(undefined);
  }, []);

  const navigate = useCallback(
    (moduleId: string, navEntityType?: string, navEntityId?: string) => {
      if (navEntityId) {
        setPendingItemId(navEntityId);
      }
      // @tested-by: tst_fe_hosted_demo_004
      // @invariant: a redirect away from an unavailable route must update the
      // router state and hash atomically; stale state must not restore it.
      setActiveModuleId(moduleId);
      setEntityType(navEntityType);
      setEntityId(navEntityId);
      window.location.hash = buildHash(moduleId, navEntityType, navEntityId);
    },
    [],
  );

  const setSelection = useCallback(
    (selEntityType: string, selEntityId: string | undefined) => {
      const nextEntityType = selEntityId ? selEntityType : undefined;
      setEntityType(nextEntityType);
      setEntityId(selEntityId);
      const target = buildHash(activeModuleId, nextEntityType, selEntityId);
      if (window.location.hash !== target) {
        window.location.hash = target;
      }
    },
    [activeModuleId],
  );

  const consumePendingItem = useCallback(() => {
    setPendingItemId(undefined);
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({
      activeModuleId,
      entityType,
      entityId,
      pendingItemId,
      setActiveModule,
      navigate,
      setSelection,
      consumePendingItem,
    }),
    [
      activeModuleId,
      entityType,
      entityId,
      pendingItemId,
      setActiveModule,
      navigate,
      setSelection,
      consumePendingItem,
    ],
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRouterContext(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new Error("useRouterContext must be used inside RouterProvider");
  }
  return ctx;
}
